import type { ChangeEventHandler, FocusEventHandler } from "react";
import { CalendarClock, CheckCircle2 } from "lucide-react";

import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui";

import { MessageError } from "../../message-error";

type SettlementStatus = "paid" | "receivable";

type SettlementStatusFieldsProps = {
  idPrefix: string;
  status: SettlementStatus;
  dueDate?: string;
  disabled?: boolean;
  statusError?: string;
  dueDateError?: string;
  dueDateTouched?: boolean;
  onStatusChange: (status: SettlementStatus) => void;
  onDueDateChange: ChangeEventHandler<HTMLInputElement>;
  onDueDateBlur: FocusEventHandler<HTMLInputElement>;
};

function localCalendarDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function SettlementStatusFields({
  idPrefix,
  status,
  dueDate = "",
  disabled = false,
  statusError,
  dueDateError,
  dueDateTouched = false,
  onStatusChange,
  onDueDateChange,
  onDueDateBlur,
}: SettlementStatusFieldsProps) {
  const paidId = `${idPrefix}-paid`;
  const receivableId = `${idPrefix}-receivable`;
  const dueDateId = `${idPrefix}-due-date`;

  return (
    <>
      <fieldset className="space-y-3" disabled={disabled}>
        <legend className="text-sm font-semibold text-zinc-800">
          Situação do pagamento
        </legend>
        <RadioGroup
          value={status}
          onValueChange={(value) =>
            onStatusChange(value as SettlementStatus)
          }
          className="grid gap-3 md:grid-cols-2"
        >
          <Label
            htmlFor={paidId}
            className="flex min-h-20 cursor-pointer items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4 transition-colors hover:border-teal-300 has-[[data-state=checked]]:border-teal-500 has-[[data-state=checked]]:bg-teal-50/60"
          >
            <RadioGroupItem id={paidId} value="paid" className="mt-0.5" />
            <CheckCircle2 className="mt-0.5 size-4 text-teal-600" />
            <span className="space-y-1">
              <span className="block font-semibold text-zinc-900">
                Recebido agora
              </span>
              <span className="block text-xs font-normal leading-relaxed text-zinc-500">
                O valor entra no realizado e no caixa desta clínica.
              </span>
            </span>
          </Label>
          <Label
            htmlFor={receivableId}
            className="flex min-h-20 cursor-pointer items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4 transition-colors hover:border-teal-300 has-[[data-state=checked]]:border-teal-500 has-[[data-state=checked]]:bg-teal-50/60"
          >
            <RadioGroupItem
              id={receivableId}
              value="receivable"
              className="mt-0.5"
            />
            <CalendarClock className="mt-0.5 size-4 text-amber-600" />
            <span className="space-y-1">
              <span className="block font-semibold text-zinc-900">
                Registrar como conta a receber
              </span>
              <span className="block text-xs font-normal leading-relaxed text-zinc-500">
                O valor fica previsto até a baixa do pagamento.
              </span>
            </span>
          </Label>
        </RadioGroup>
        <MessageError messageError={statusError} />
      </fieldset>

      {status === "receivable" && !disabled && (
        <div className="max-w-xs space-y-1">
          <Label htmlFor={dueDateId} required>
            Vencimento
          </Label>
          <Input
            id={dueDateId}
            name="due_date"
            type="date"
            min={localCalendarDate(new Date())}
            value={dueDate}
            onChange={onDueDateChange}
            onBlur={onDueDateBlur}
            isInvalid={dueDateTouched && dueDateError}
            isValid={dueDateTouched && !dueDateError}
            messageError={dueDateError}
          />
        </div>
      )}
    </>
  );
}
